import type { Dayjs } from 'dayjs';
import type { EmployeeTimesheet } from './types';

export interface EmployeeSummary {
  total: string;
  overtime: number;
  violation: number;
  leave: string;
  mealAllowance: number;
  actualTotal: string;
}

// Helper dùng chung cho nhiều nơi (bảng công, export Excel, ...):
// kiểm tra một ngày có phải Chủ nhật hay không.
export const isSunday = (date: Dayjs): boolean => date.day() === 0;

export const calculateSummary = (emp: EmployeeTimesheet): EmployeeSummary => {

  if (emp.backendSummary) {
    const backend = emp.backendSummary;
    /**
     * New behavior:
     * - Cột "Tổng": tổng số ngày làm việc trong tháng (không tính Chủ nhật), ví dụ tháng có 30 ngày, 4 Chủ nhật → 26.
     *   Giá trị này được backend tính sẵn và trả về dưới dạng `totalWorkingDaysInMonth`.
     * - Cột "Nghỉ": số ngày nghỉ thực tế (lấy từ backend.totalLeaveDays).
     * - Cột "Tổng thực tế": tổng hệ số công = tổng (workingHours / 8) cho các ngày có chấm công + 1.0 cho mỗi ngày nghỉ có lương.
     *   Ví dụ: ngày 4 hệ số 1.000 + ngày 6 hệ số 0.625 + ngày 7 nghỉ có lương 1.0 = 2.625.
     *
     * Các cột khác giữ nguyên:
     * - Tăng ca: tổng giờ tăng ca trong tháng.
     * - Vi phạm: tổng phút vi phạm.
     * - Phụ cấp: tổng số ngày đủ điều kiện phụ cấp cơm.
     */

    const totalWorkingDaysInMonth: number = Number(backend.totalWorkingDaysInMonth ?? 0);

    const totalDaysWorked: number = Number(backend.totalDaysWorked ?? 0);
    // Số ngày nghỉ thực tế trong tháng (bao gồm có lương và không lương) lấy trực tiếp từ backend
    const leaveDays: number = Number(backend.totalLeaveDays ?? 0);

    return {
      // Hiển thị với 1 chữ số thập phân (ví dụ: 1.0, 5.0)
      total: totalWorkingDaysInMonth.toFixed(1),
      overtime: Math.round(backend.totalOvertimeHours * 10) / 10,
      violation: backend.totalViolationMinutes,
      // Hiển thị đúng số ngày nghỉ, 1 chữ số thập phân
      leave: leaveDays.toFixed(1),
      mealAllowance: backend.totalMealAllowanceDays,
      // Tổng thực tế = tổng hệ số công (hiển thị 3 chữ số thập phân, ví dụ: 2.625)
      actualTotal: totalDaysWorked.toFixed(3),
    };
  }

  // Nếu vì lý do nào đó backend không trả summary, frontend sẽ hiển thị 0
  return {
    total: '0.0',
    overtime: 0,
    violation: 0,
    leave: '0.0',
    mealAllowance: 0,
    actualTotal: '0.000',
  };
};


